import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';

import { postContaboUploads } from '../../../redux/features/generalSlice';

/**
 * A picture for a KG question — either a character or an uploaded file.
 *
 * ---------------------------------------------------------------------------
 * WHY ONE FIELD ACCEPTS BOTH
 *
 * The KG paper's pictures are stored as a single string, and the renderer
 * decides how to draw it by looking for a "/":
 *
 *     "COW emoji"                    -> rendered as text, at picture size
 *     "https://.../cow.png"          -> rendered as an <img>
 *
 * That is not a shortcut, it is the right model for this paper. Question 3 asks
 * which sound a Malayalam letter makes and shows the letterform itself — a
 * glyph renders far more crisply as type than any bitmap of it would, and it
 * stays sharp at every screen density. Emoji are placeholders until real
 * artwork arrives, and the two have to coexist during that transition.
 *
 * So the editor gets both affordances in one control rather than a type
 * selector they would have to understand first: type a character, or upload a
 * file. The preview shows exactly what the child will see.
 *
 * ---------------------------------------------------------------------------
 * WHY SVG IS REFUSED
 *
 * An SVG is a document that can carry script, and these images are rendered on
 * a public, unauthenticated page. The student portal's own file service already
 * refuses to serve .svg and .html inline for this reason. A hero image or a
 * picture of a cow is never worth that, so the picker rejects them at selection
 * time — where the message can explain itself — rather than letting the upload
 * succeed and fail silently at render.
 */

// Kept deliberately narrow. Every one of these is a raster format a browser
// draws without executing anything.
const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

const MAX_BYTES = 2 * 1024 * 1024;

const isUploaded = (value) => typeof value === 'string' && value.includes('/');

const ImageField = ({
  value,
  imageKey,
  onChange,
  label = 'Picture',
  helperText,
  folderName = 'assessment/kg',
  size = 56,
}) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    // Reset immediately so picking the same file twice still fires onChange.
    event.target.value = '';
    if (!file) return;

    if (!ACCEPTED.includes(file.type)) {
      enqueueSnackbar(
        `${file.type || 'That file'} is not allowed. Use a PNG, JPG, WEBP or GIF — ` +
          `SVG files can carry scripts and this artwork is shown on a public page.`,
        { variant: 'error' }
      );
      return;
    }
    if (file.size > MAX_BYTES) {
      enqueueSnackbar(
        `That picture is ${(file.size / 1024 / 1024).toFixed(1)} MB. Keep it under 2 MB — ` +
          `it is rendered at about ${size}px, so a large file only slows the page down.`,
        { variant: 'error' }
      );
      return;
    }

    const formData = new FormData();
    // The upload middleware reads the field name "files" and the folder from
    // `folderName`; both are contracts with node-portal's uploadContabo.js.
    formData.append('files', file);
    formData.append('folderName', folderName);

    setUploading(true);
    try {
      const res = await dispatch(postContaboUploads(formData)).unwrap();
      const uploaded = (res?.data || res)?.[0];
      if (!uploaded?.file_url) throw new Error(res?.message || 'Upload failed');
      // Store the public URL for rendering AND the object key beside it. The
      // URL alone gives no handle on the object later — the key is what a
      // future replace or delete needs.
      onChange(uploaded.file_url, uploaded.file_key);
      enqueueSnackbar('Picture uploaded', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err?.message || 'Could not upload the picture', { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const uploaded = isUploaded(value);

  return (
    <Stack spacing={0.75}>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box
          sx={{
            width: size,
            height: size,
            flexShrink: 0,
            borderRadius: 1.5,
            border: '1px dashed',
            borderColor: value ? 'divider' : 'grey.400',
            bgcolor: 'background.neutral',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            fontSize: size * 0.55,
            lineHeight: 1,
          }}
        >
          {uploading && <CircularProgress size={20} />}
          {!uploading && uploaded && (
            <Box
              component="img"
              src={value}
              alt=""
              sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          )}
          {/* A bare glyph is drawn as text, exactly as the paper draws it. */}
          {!uploading && !uploaded && value && <span>{value}</span>}
          {!uploading && !value && (
            <Icon icon="mdi:image-outline" width={size * 0.4} color="#9e9e9e" />
          )}
        </Box>

        <Stack spacing={0.75} sx={{ flex: 1 }}>
          <TextField
            size="small"
            fullWidth
            label={label}
            value={uploaded ? '' : value || ''}
            placeholder="Paste an emoji or a letter"
            disabled={uploaded}
            onChange={(e) => onChange(e.target.value, null)}
            helperText={
              uploaded
                ? 'Using an uploaded picture. Remove it to type a character instead.'
                : helperText
            }
            InputProps={{
              endAdornment: value ? (
                <Tooltip title="Clear">
                  <IconButton size="small" onClick={() => onChange('', null)}>
                    <Icon icon="mdi:close" width={16} />
                  </IconButton>
                </Tooltip>
              ) : null,
            }}
          />

          <Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Icon icon="mdi:upload" width={16} />}
              disabled={uploading}
              onClick={() => inputRef.current?.click()}
            >
              {uploaded ? 'Replace picture' : 'Upload a picture'}
            </Button>
            <input
              ref={inputRef}
              type="file"
              hidden
              accept={ACCEPTED.join(',')}
              onChange={handleFile}
            />
          </Box>
        </Stack>
      </Stack>

      {uploaded && imageKey && (
        <Typography variant="caption" color="text.disabled" sx={{ wordBreak: 'break-all' }}>
          {imageKey}
        </Typography>
      )}
    </Stack>
  );
};

export default ImageField;
